import { useCallback } from 'react';

import type { EventData, EventOptions, Events } from '@coursera/event-pulse/sdk';
import { track } from '@coursera/event-pulse/sdk';

/**
 * This hook provides a function that tracks the given event with the given data and options.
 *
 * The returned function is memoized on the values of `data` and `options`, so it is safe to use in dependency arrays.
 *
 * To track different events from the same component, use `useTracker` instead.
 */
function useBoundTracker<K extends keyof Events>(
  eventName: K,
  data: EventData<K>,
  options?: EventOptions<K>
): () => void {
  return useCallback<() => void>(
    () => track(eventName, data, options),
    // data and options are compared by value, so passing a new object literal on every render doesn't re-create this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventName, JSON.stringify(data), JSON.stringify(options)]
  );
}

export default useBoundTracker;
